import { ForwardedRef, forwardRef } from 'react';
import {
  CounterBadge,
  CounterBadgeProps,
} from '@allergan-data-labs/alle-elements-counter-badge';
import { useColorMode } from '@allergan-data-labs/alle-elements-core';
import { Button, ButtonProps } from './button';
import {
  getButtonCounterBadgeStyles,
  getButtonCounterStyles,
} from './button-counter.theme';

export type ButtonCounterBadgeProps = {
  isDisabled?: ButtonProps['isDisabled'];
  buttonSize?: ButtonProps['size'];
  buttonColorScheme?: ButtonProps['colorScheme'];
  buttonVariant?: ButtonProps['variant'];
} & CounterBadgeProps;

const ButtonCounterBadge = ({
  count,
  colorMode,
  size,
  isDisabled,
  buttonSize,
  buttonColorScheme,
  buttonVariant,
  ...props
}: ButtonCounterBadgeProps) => {
  return (
    <CounterBadge
      sx={getButtonCounterBadgeStyles({
        count,
        colorMode,
        size,
        isDisabled,
        buttonSize,
        buttonColorScheme,
        buttonVariant,
      })}
      count={count}
      {...props}
    />
  );
};

export type ButtonCounterProps = {
  count?: number;
} & Omit<ButtonProps, 'rightIcon'>;

/**
 * Button Counter is used to display a count of the amount of filters selected inside of the button to which the button pertains too.
 * It is commonly found used within Filter Type components such as Side Tray Filter and Sidebar Filter. In general, it follows the same
 * rules that the <Button-Common> does.
 */
export const ButtonCounter = forwardRef<HTMLButtonElement, ButtonCounterProps>(
  (
    {
      children,
      colorMode: propColorMode,
      count = 0,
      size,
      colorScheme,
      isDisabled,
      variant,
      ...props
    },
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    return (
      <Button
        sx={getButtonCounterStyles({ colorMode, colorScheme, variant })}
        colorMode={colorMode}
        isDisabled={isDisabled}
        size={size}
        colorScheme={colorScheme}
        variant={variant}
        {...props}
        ref={ref}
      >
        {children}
        <ButtonCounterBadge
          buttonSize={size}
          colorMode={colorMode}
          isDisabled={isDisabled}
          count={count}
          buttonColorScheme={colorScheme}
          buttonVariant={variant}
        />
      </Button>
    );
  }
);
